import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Page,
  Layout,
  Card,
  Text,
  Select,
  Spinner,
  Grid,
  Badge,
  Button,
  InlineStack
} from '@shopify/polaris'
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts'

import { useAuth } from '@/contexts/AuthContext'
import { analyticsApi } from '@/services/api'
import type { AnalyticsPeriod } from '@/types'

export default function AnalyticsPage() {
  const { user } = useAuth()
  const [period, setPeriod] = useState<AnalyticsPeriod>('30d')

  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['analytics-dashboard', user?.storeId, period],
    queryFn: () => analyticsApi.getDashboard(user!.storeId, period),
    enabled: !!user?.storeId
  })

  const { data: conversationData, isLoading: conversationLoading } = useQuery({
    queryKey: ['analytics-conversations', user?.storeId, period],
    queryFn: () => analyticsApi.getConversationAnalytics(user!.storeId, period),
    enabled: !!user?.storeId
  })

  const { data: messageData, isLoading: messageLoading } = useQuery({
    queryKey: ['analytics-messages', user?.storeId, period],
    queryFn: () => analyticsApi.getMessageAnalytics(user!.storeId, period),
    enabled: !!user?.storeId
  })

  const { data: revenueData, isLoading: revenueLoading } = useQuery({
    queryKey: ['analytics-revenue', user?.storeId, period],
    queryFn: () => analyticsApi.getRevenueAnalytics(user!.storeId, period),
    enabled: !!user?.storeId
  })

  const periodOptions = [
    { label: 'Last 7 days', value: '7d' },
    { label: 'Last 30 days', value: '30d' }
  ]

  const handleExport = async (type: 'conversations' | 'messages' | 'customers') => {
    try {
      const response = await analyticsApi.exportData(user!.storeId, type, 'csv', period)
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${type}-${period}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Export failed:', error)
    }
  }

  const isLoading = dashboardLoading || conversationLoading || messageLoading || revenueLoading

  if (isLoading) {
    return (
      <Page title="Analytics">
        <Layout>
          <Layout.Section>
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <Spinner accessibilityLabel="Loading analytics" size="large" />
            </div>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  const dashboard = dashboardData?.data.data
  const conversations = conversationData?.data.data
  const messages = messageData?.data.data
  const revenue = revenueData?.data.data

  // Prepare chart data
  const conversationChartData = conversations?.dailyConversations?.map((item: any) => ({
    date: new Date(item.date).toLocaleDateString(),
    conversations: item.count
  })) || []

  const messageChartData = messages?.dailyMessages?.reduce((acc: any[], item: any) => {
    const existingDate = acc.find(d => d.date === new Date(item.date).toLocaleDateString())
    if (existingDate) {
      existingDate[item.direction.toLowerCase()] = item.count
    } else {
      acc.push({
        date: new Date(item.date).toLocaleDateString(),
        [item.direction.toLowerCase()]: item.count
      })
    }
    return acc
  }, []) || []

  const revenueChartData = revenue?.dailyRevenue?.map((item: any) => ({
    date: new Date(item.date).toLocaleDateString(),
    revenue: parseFloat(item.revenue || 0),
    orders: item.order_count
  })) || []

  const conversationStatusData = conversations?.resolutionStats?.map((item: any) => ({
    name: item.status,
    value: item._count.id
  })) || []

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8']

  return (
    <Page 
      title="Analytics"
      subtitle="Track your WhatsApp CRM performance"
      primaryAction={{
        content: 'Export Data',
        onAction: () => handleExport('conversations')
      }}
    >
      <Layout>
        <Layout.Section>
          <Card>
            <div style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text variant="headingMd" as="h2">
                Performance Overview
              </Text>
              <Select
                label=""
                options={periodOptions}
                value={period}
                onChange={(value) => setPeriod(value as AnalyticsPeriod)}
              />
            </div>
          </Card>
        </Layout.Section>

        {/* Key Metrics */}
        <Layout.Section>
          <Grid>
            <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <Card>
                <div style={{ padding: '16px' }}>
                  <Text variant="headingLg" as="h3">
                    {dashboard?.metrics.conversations?.reduce((sum, item) => sum + item._count.id, 0) || 0}
                  </Text>
                  <Text variant="bodyMd" as="p" color="subdued">
                    Total Conversations
                  </Text>
                  <div style={{ marginTop: '8px' }}>
                    <Badge status="info">
                      {dashboard?.metrics.conversations?.find(item => item.status === 'NEW')?._count.id || 0} New
                    </Badge>
                  </div>
                </div>
              </Card>
            </Grid.Cell>

            <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <Card>
                <div style={{ padding: '16px' }}>
                  <Text variant="headingLg" as="h3">
                    {dashboard?.metrics.messages?.reduce((sum, item) => sum + item._count.id, 0) || 0}
                  </Text>
                  <Text variant="bodyMd" as="p" color="subdued">
                    Total Messages
                  </Text>
                  <div style={{ marginTop: '8px' }}>
                    <Badge status="success">
                      {dashboard?.metrics.messages?.find(item => item.direction === 'OUTBOUND')?._count.id || 0} Sent
                    </Badge>
                  </div>
                </div>
              </Card>
            </Grid.Cell>

            <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <Card>
                <div style={{ padding: '16px' }}>
                  <Text variant="headingLg" as="h3">
                    {dashboard?.metrics.customers || 0}
                  </Text>
                  <Text variant="bodyMd" as="p" color="subdued">
                    Active Customers
                  </Text>
                  <div style={{ marginTop: '8px' }}>
                    <Badge status="attention">WhatsApp</Badge>
                  </div>
                </div>
              </Card>
            </Grid.Cell>

            <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 3, xl: 3 }}>
              <Card>
                <div style={{ padding: '16px' }}>
                  <Text variant="headingLg" as="h3">
                    ${dashboard?.metrics.orders.revenue?.toFixed(2) || '0.00'}
                  </Text>
                  <Text variant="bodyMd" as="p" color="subdued">
                    Revenue Generated
                  </Text>
                  <div style={{ marginTop: '8px' }}>
                    <Badge status="success">
                      {dashboard?.metrics.orders.count || 0} Orders
                    </Badge>
                  </div>
                </div>
              </Card>
            </Grid.Cell>
          </Grid>
        </Layout.Section>

        {/* Charts */}
        <Layout.Section>
          <Grid>
            <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 8, xl: 8 }}>
              <Card>
                <div style={{ padding: '16px' }}>
                  <Text variant="headingMd" as="h3">
                    Conversations Over Time
                  </Text>
                  <div style={{ height: '300px', marginTop: '16px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={conversationChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Line 
                          type="monotone" 
                          dataKey="conversations" 
                          stroke="#007ace" 
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </Card>
            </Grid.Cell>

            <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 4, xl: 4 }}>
              <Card>
                <div style={{ padding: '16px' }}>
                  <Text variant="headingMd" as="h3">
                    Conversation Status
                  </Text>
                  <div style={{ height: '300px', marginTop: '16px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={conversationStatusData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {conversationStatusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </Card>
            </Grid.Cell>
          </Grid>
        </Layout.Section>

        <Layout.Section>
          <Grid>
            <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <Card>
                <div style={{ padding: '16px' }}>
                  <Text variant="headingMd" as="h3">
                    Message Activity
                  </Text>
                  <div style={{ height: '300px', marginTop: '16px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={messageChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="inbound" fill="#00C49F" name="Received" />
                        <Bar dataKey="outbound" fill="#007ace" name="Sent" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </Card>
            </Grid.Cell>

            <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
              <Card>
                <div style={{ padding: '16px' }}>
                  <Text variant="headingMd" as="h3">
                    Revenue Trend
                  </Text>
                  <div style={{ height: '300px', marginTop: '16px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={revenueChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip formatter={(value, name) => [
                          name === 'revenue' ? `$${value}` : value,
                          name === 'revenue' ? 'Revenue' : 'Orders'
                        ]} />
                        <Line 
                          type="monotone" 
                          dataKey="revenue" 
                          stroke="#00C49F" 
                          strokeWidth={2}
                          name="revenue"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </Card>
            </Grid.Cell>
          </Grid>
        </Layout.Section>

        {/* Export Actions */}
        <Layout.Section>
          <Card>
            <div style={{ padding: '16px' }}>
              <Text variant="headingMd" as="h3">
                Export Data
              </Text>
              <div style={{ marginTop: '16px' }}>
                <InlineStack gap="200">
                  <Button onClick={() => handleExport('conversations')}>
                    Export Conversations
                  </Button>
                  <Button onClick={() => handleExport('messages')}>
                    Export Messages
                  </Button>
                  <Button onClick={() => handleExport('customers')}>
                    Export Customers
                  </Button>
                </InlineStack>
              </div>
            </div>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  )
}